import React, { memo, useCallback, useEffect, useRef, useState } from "react";
import { ReactSVG } from "react-svg";

import { markNotificationRead } from "../../api/notificationsService";
import { openPopup } from "../Common/popupUIStore";
import {
   CHAT_MESSAGE_TOAST_AUTO_DISMISS_MS,
   NOTIFICATION_TOAST_AUTO_DISMISS_MS,
} from "../../constants/notificationToast";
import useChatMessageToasts from "../../hooks/useChatMessageToasts";
import useNotificationToasts from "../../hooks/useNotificationToasts";
import { formatChatToastTime } from "../../utils/chatMessageFormat";
import { triggerHapticFeedback } from "../../utils/hapticFeedback";
import {
   formatNotificationTime,
   notificationBody,
   notificationCreatedAt,
   notificationId,
   notificationTitle,
   notificationVideoMeta,
   videoNotificationLead,
} from "../../utils/notificationsFormat";

import arrowIcon from "../../assets/svg/arrow-s.svg";
import bellIcon from "../../assets/svg/mingcute--notification-line.svg";
import messageIcon from "../../assets/svg/tabler--message.svg";

const SWIPE_DISMISS_Y_PX = 56;
const SWIPE_DISMISS_X_RATIO = 0.32;

function NotificationToastItem({ item, onDismiss }) {
   const [entered, setEntered] = useState(false);
   const [leaving, setLeaving] = useState(false);
   const [leaveDir, setLeaveDir] = useState("up");
   const [drag, setDrag] = useState({ x: 0, y: 0 });
   const [isDragging, setIsDragging] = useState(false);
   const dragRef = useRef(null);
   const idleTimerRef = useRef(null);
   const dragStateRef = useRef({
      active: false,
      startX: 0,
      startY: 0,
      pointerId: null,
      moved: false,
   });
   const touchedRef = useRef(false);

   const title = notificationTitle(item);
   const body = notificationBody(item);
   const videoMeta = notificationVideoMeta(item);
   const time = formatNotificationTime(notificationCreatedAt(item));

   const clearIdleTimer = useCallback(() => {
      if (idleTimerRef.current) {
         clearTimeout(idleTimerRef.current);
         idleTimerRef.current = null;
      }
   }, []);

   const markTouched = useCallback(() => {
      if (touchedRef.current) return;
      touchedRef.current = true;
      clearIdleTimer();
   }, [clearIdleTimer]);

   const startLeave = useCallback(
      (direction = "up", options = {}) => {
         if (leaving) return;
         clearIdleTimer();
         if (!options.keepDrag) {
            setDrag({ x: 0, y: 0 });
         }
         setLeaveDir(direction);
         if (options.fromSwipe) {
            triggerHapticFeedback(70);
         }
         setLeaving(true);
      },
      [clearIdleTimer, leaving],
   );

   useEffect(() => {
      touchedRef.current = false;
      clearIdleTimer();

      idleTimerRef.current = setTimeout(() => {
         if (!touchedRef.current) {
            startLeave("up");
         }
      }, NOTIFICATION_TOAST_AUTO_DISMISS_MS);

      return clearIdleTimer;
   }, [clearIdleTimer, startLeave]);

   const handleTransitionEnd = (event) => {
      if (event.target !== dragRef.current || !leaving) return;
      onDismiss();
   };

   const handleOpenVideo = (event) => {
      event.stopPropagation();
      const href = String(videoMeta?.watchUrl || "").trim();
      if (href) {
         window.open(href, "_blank", "noopener,noreferrer");
      }
      const id = notificationId(item);
      if (id != null) {
         markNotificationRead(id).catch(() => {});
      }
      startLeave("up");
   };

   const handleOpenNotifications = (event) => {
      event.stopPropagation();
      markTouched();
      openPopup("notifications");
   };

   const isInteractiveTarget = (target) =>
      Boolean(target?.closest?.("button, a, input, textarea"));

   const handlePointerDown = (event) => {
      if (leaving || isInteractiveTarget(event.target)) return;
      setEntered(true);
      markTouched();
      setIsDragging(true);
      dragStateRef.current = {
         active: true,
         startX: event.clientX,
         startY: event.clientY,
         pointerId: event.pointerId,
         moved: false,
      };
      try {
         event.currentTarget.setPointerCapture(event.pointerId);
      } catch {}
   };

   const handlePointerMove = (event) => {
      if (
         !dragStateRef.current.active ||
         dragStateRef.current.pointerId !== event.pointerId
      ) {
         return;
      }

      const dx = event.clientX - dragStateRef.current.startX;
      const dy = event.clientY - dragStateRef.current.startY;

      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) {
         dragStateRef.current.moved = true;
      }

      setDrag({
         x: dx,
         y: dy < 0 ? dy : dy * 0.12,
      });
   };

   const handlePointerUp = (event) => {
      if (
         !dragStateRef.current.active ||
         dragStateRef.current.pointerId !== event.pointerId
      ) {
         return;
      }

      const dx = event.clientX - dragStateRef.current.startX;
      const dy = event.clientY - dragStateRef.current.startY;
      const width = dragRef.current?.offsetWidth || 320;
      const dismissX = Math.abs(dx) >= Math.max(72, width * SWIPE_DISMISS_X_RATIO);
      const dismissY = dy <= -SWIPE_DISMISS_Y_PX;

      try {
         event.currentTarget.releasePointerCapture(event.pointerId);
      } catch {}

      dragStateRef.current = {
         active: false,
         startX: 0,
         startY: 0,
         pointerId: null,
         moved: false,
      };
      setIsDragging(false);

      if (dismissX) {
         startLeave(dx > 0 ? "right" : "left", {
            fromSwipe: true,
            keepDrag: true,
         });
         return;
      }

      if (dismissY) {
         startLeave("up", { fromSwipe: true, keepDrag: true });
         return;
      }

      setDrag({ x: 0, y: 0 });
   };

   const previewMeta = videoMeta
      ? videoNotificationLead(body, videoMeta.title)
      : body || "";

   const dismissProgress = leaving ? 1 : 0;

   let transform = "";
   if (leaving) {
      if (leaveDir === "left") transform = "translate3d(-120vw, 0, 0)";
      else if (leaveDir === "right") transform = "translate3d(120vw, 0, 0)";
      else transform = "translate3d(0, -120vh, 0)";
   } else if (entered && (drag.x || drag.y)) {
      transform = `translate3d(${drag.x}px, ${drag.y}px, 0)`;
   }

   const dragStyle = {
      "--dismiss-progress": dismissProgress,
   };
   if (transform) dragStyle.transform = transform;
   if (leaving) dragStyle.opacity = 0.85;

   const handleEnterAnimationEnd = (event) => {
      if (event.target !== dragRef.current) return;
      if (event.animationName !== "notificationToastEnter") return;
      setEntered(true);
   };

   return (
      <div
         ref={dragRef}
         className={
            "notificationToastDrag" +
            (entered ? " notificationToastDrag--entered" : "") +
            (leaving ? ` notificationToastDrag--leaving notificationToastDrag--leave-${leaveDir}` : "") +
            (isDragging || drag.x || drag.y
               ? " notificationToastDrag--dragging"
               : "")
         }
         style={dragStyle}
         onTransitionEnd={handleTransitionEnd}
         onAnimationEnd={handleEnterAnimationEnd}
         onPointerDown={handlePointerDown}
         onPointerMove={handlePointerMove}
         onPointerUp={handlePointerUp}
         onPointerCancel={handlePointerUp}
      >
         <div
            className={
               "notificationToast" +
               (videoMeta ? " notificationToast--video" : "")
            }
            role="status"
            aria-live="polite"
         >
            <div className="notificationToast__icon" aria-hidden="true">
               <ReactSVG className="notificationToast__iconSvg" src={bellIcon} />
            </div>

            <div className="notificationToast__info">
               <div className="notificationToast__head">
                  <h3 className="notificationToast__title">{title}</h3>
                  {time ? (
                     <span className="notificationToast__time">{time}</span>
                  ) : null}
               </div>
               {previewMeta ? (
                  <p className="notificationToast__meta">{previewMeta}</p>
               ) : null}
            </div>

            <button
               type="button"
               className="notificationToast__chev"
               aria-label="Deschide notificările"
               title="Notificări"
               onClick={handleOpenNotifications}
            >
               <ReactSVG className="notificationToast__chevIcon" src={arrowIcon} />
            </button>

            {videoMeta ? (
               <button
                  type="button"
                  className="notificationToast__videoBtn"
                  onClick={handleOpenVideo}
               >
                  Deschide
               </button>
            ) : null}
         </div>
      </div>
   );
}

function ChatMessageToastItem({ item, onDismiss }) {
   const [entered, setEntered] = useState(false);
   const [leaving, setLeaving] = useState(false);
   const [leaveDir, setLeaveDir] = useState("up");
   const [drag, setDrag] = useState({ x: 0, y: 0 });
   const [isDragging, setIsDragging] = useState(false);
   const dragRef = useRef(null);
   const idleTimerRef = useRef(null);
   const dragStateRef = useRef({
      active: false,
      startX: 0,
      startY: 0,
      pointerId: null,
      moved: false,
   });
   const touchedRef = useRef(false);
   const openedRef = useRef(false);

   const title = String(item?.title || "Mesaj nou");
   const body = String(item?.body || "");
   const time = formatChatToastTime(item?.createdAt);

   const clearIdleTimer = useCallback(() => {
      if (idleTimerRef.current) {
         clearTimeout(idleTimerRef.current);
         idleTimerRef.current = null;
      }
   }, []);

   const markTouched = useCallback(() => {
      if (touchedRef.current) return;
      touchedRef.current = true;
      clearIdleTimer();
   }, [clearIdleTimer]);

   const startLeave = useCallback(
      (direction = "up", options = {}) => {
         if (leaving) return;
         clearIdleTimer();
         if (!options.keepDrag) {
            setDrag({ x: 0, y: 0 });
         }
         setLeaveDir(direction);
         if (options.fromSwipe) {
            triggerHapticFeedback(70);
         }
         setLeaving(true);
      },
      [clearIdleTimer, leaving],
   );

   useEffect(() => {
      touchedRef.current = false;
      openedRef.current = false;
      clearIdleTimer();

      idleTimerRef.current = setTimeout(() => {
         if (!touchedRef.current) {
            startLeave("up");
         }
      }, CHAT_MESSAGE_TOAST_AUTO_DISMISS_MS);

      return clearIdleTimer;
   }, [clearIdleTimer, startLeave, item?.messageId]);

   const handleTransitionEnd = (event) => {
      if (event.target !== dragRef.current || !leaving) return;
      onDismiss();
   };

   const handleOpenChat = (event) => {
      event?.preventDefault?.();
      event?.stopPropagation?.();
      if (leaving || openedRef.current) return;
      openedRef.current = true;
      markTouched();
      openPopup("staffChat", {
         openThread: Boolean(item?.conversationId && item?.student),
         conversationId: item?.conversationId ?? null,
         student: item?.student ?? null,
      });
      window.setTimeout(() => startLeave("up"), 0);
   };

   const isInteractiveTarget = (target) =>
      Boolean(target?.closest?.("button, a, input, textarea"));

   const handlePointerDown = (event) => {
      if (leaving || isInteractiveTarget(event.target)) return;
      setEntered(true);
      markTouched();
      setIsDragging(true);
      dragStateRef.current = {
         active: true,
         startX: event.clientX,
         startY: event.clientY,
         pointerId: event.pointerId,
         moved: false,
      };
      try {
         event.currentTarget.setPointerCapture(event.pointerId);
      } catch {}
   };

   const handlePointerMove = (event) => {
      if (
         !dragStateRef.current.active ||
         dragStateRef.current.pointerId !== event.pointerId
      ) {
         return;
      }

      const dx = event.clientX - dragStateRef.current.startX;
      const dy = event.clientY - dragStateRef.current.startY;

      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) {
         dragStateRef.current.moved = true;
      }

      setDrag({
         x: dx,
         y: dy < 0 ? dy : dy * 0.12,
      });
   };

   const handlePointerUp = (event) => {
      if (
         !dragStateRef.current.active ||
         dragStateRef.current.pointerId !== event.pointerId
      ) {
         return;
      }

      const dx = event.clientX - dragStateRef.current.startX;
      const dy = event.clientY - dragStateRef.current.startY;
      const width = dragRef.current?.offsetWidth || 320;
      const dismissX = Math.abs(dx) >= Math.max(72, width * SWIPE_DISMISS_X_RATIO);
      const dismissY = dy <= -SWIPE_DISMISS_Y_PX;
      const moved = dragStateRef.current.moved;

      try {
         event.currentTarget.releasePointerCapture(event.pointerId);
      } catch {}

      dragStateRef.current = {
         active: false,
         startX: 0,
         startY: 0,
         pointerId: null,
         moved: false,
      };
      setIsDragging(false);

      if (dismissX) {
         startLeave(dx > 0 ? "right" : "left", {
            fromSwipe: true,
            keepDrag: true,
         });
         return;
      }

      if (dismissY) {
         startLeave("up", { fromSwipe: true, keepDrag: true });
         return;
      }

      if (!moved) {
         handleOpenChat(event);
         return;
      }

      setDrag({ x: 0, y: 0 });
   };

   const dismissProgress = leaving ? 1 : 0;

   let transform = "";
   if (leaving) {
      if (leaveDir === "left") transform = "translate3d(-120vw, 0, 0)";
      else if (leaveDir === "right") transform = "translate3d(120vw, 0, 0)";
      else transform = "translate3d(0, -120vh, 0)";
   } else if (entered && (drag.x || drag.y)) {
      transform = `translate3d(${drag.x}px, ${drag.y}px, 0)`;
   }

   const dragStyle = {
      "--dismiss-progress": dismissProgress,
   };
   if (transform) dragStyle.transform = transform;
   if (leaving) dragStyle.opacity = 0.85;

   const handleEnterAnimationEnd = (event) => {
      if (event.target !== dragRef.current) return;
      if (event.animationName !== "notificationToastEnter") return;
      setEntered(true);
   };

   return (
      <div
         ref={dragRef}
         className={
            "notificationToastDrag" +
            (entered ? " notificationToastDrag--entered" : "") +
            (leaving ? ` notificationToastDrag--leaving notificationToastDrag--leave-${leaveDir}` : "") +
            (isDragging || drag.x || drag.y
               ? " notificationToastDrag--dragging"
               : "")
         }
         style={dragStyle}
         onTransitionEnd={handleTransitionEnd}
         onAnimationEnd={handleEnterAnimationEnd}
         onPointerDown={handlePointerDown}
         onPointerMove={handlePointerMove}
         onPointerUp={handlePointerUp}
         onPointerCancel={handlePointerUp}
      >
         <div
            className="notificationToast notificationToast--chat notificationToast--clickable"
            role="button"
            tabIndex={0}
            aria-label="Deschide mesajele"
            onClick={handleOpenChat}
            onKeyDown={(event) => {
               if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  handleOpenChat(event);
               }
            }}
         >
            <div className="notificationToast__icon" aria-hidden="true">
               <ReactSVG
                  className="notificationToast__iconSvg"
                  src={messageIcon}
               />
            </div>

            <div className="notificationToast__info">
               <div className="notificationToast__head">
                  <h3 className="notificationToast__title">{title}</h3>
                  {time ? (
                     <span className="notificationToast__time">{time}</span>
                  ) : null}
               </div>
               {body ? <p className="notificationToast__meta">{body}</p> : null}
            </div>

            <button
               type="button"
               className="notificationToast__chev"
               aria-label="Deschide conversația"
               title="Mesaje"
               onClick={handleOpenChat}
               onPointerDown={(event) => event.stopPropagation()}
            >
               <ReactSVG className="notificationToast__chevIcon" src={arrowIcon} />
            </button>
         </div>
      </div>
   );
}

function NotificationToastStack() {
   const notifications = useNotificationToasts();
   const chat = useChatMessageToasts();

   if (notifications.canAccess && notifications.current) {
      return (
         <div
            className="notificationToastStack"
            role="region"
            aria-label="Notificări noi"
         >
            <NotificationToastItem
               key={String(notificationId(notifications.current) ?? "toast")}
               item={notifications.current}
               onDismiss={notifications.dismissCurrent}
            />
         </div>
      );
   }

   if (chat.canAccess && chat.current) {
      return (
         <div
            className="notificationToastStack"
            role="region"
            aria-label="Mesaje noi"
         >
            <ChatMessageToastItem
               key={String(chat.current.messageId ?? "chat-toast")}
               item={chat.current}
               onDismiss={chat.dismissCurrent}
            />
         </div>
      );
   }

   return null;
}

export default memo(NotificationToastStack);
